/**
 * Shared interaction styling for `LockedFeature`'s shells and
 * `UpgradeCtaButton` — one definition so the badge, row and CTA cannot drift
 * apart on transition, press feedback or focus ring.
 */

/**
 * Reduced-motion-safe transition, matching the established pattern in
 * `DigestRestaurantLead.tsx`/`GoingToPressState.tsx`: a scoped `@media`
 * counterpart on the SAME selector, never a blanket override.
 */
export const INTERACTIVE_TRANSITION_SX = {
  // Only properties that interpolate: `background-color` (the tile/row hover
  // surfaces are colours for exactly this reason) and `transform` (press).
  transition: 'transform 0.15s ease, background-color 0.15s ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const;

/**
 * `:focus-visible` ring. The colour is mode-paired (`--locked-focus-ring`:
 * purple.500 light, purple.300 dark) and the inner 2px gap is `--card-bg`
 * (white in light mode, elevated dark surface in dark mode — NOT a fixed
 * black, which would read as a black ring on a light card). The transparent
 * `outline` is deliberate: Windows forced-colors mode discards `box-shadow`,
 * and a transparent outline is repainted in a system colour there, so the
 * ring never disappears.
 */
export const FOCUS_RING_STYLE = {
  boxShadow: '0 0 0 2px var(--card-bg), 0 0 0 4px var(--locked-focus-ring)',
  outline: '2px solid transparent',
  outlineOffset: '2px',
} as const;

export const FOCUS_RING_SX = { _focusVisible: FOCUS_RING_STYLE } as const;

/** Press feedback for the tappable shells and the CTA; covered by the reduced-motion rule above. */
export const PRESS_FEEDBACK = { transform: 'scale(0.98)' } as const;
